







































import gql from 'graphql-tag';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import {
  chain,
  difference,
  filter,
  find,
  flow,
  identity,
  includes,
  isEqual,
  keyBy,
  map,
  mapKeys,
  omit,
  pick,
  pickBy,
  startsWith,
} from 'lodash';
import { mapOrder } from '@/utils';

import ToDoListSearchQuery from '@/graphql/todo_list/ToDoListListing';
import { getCustomAttributeGqlTypeName } from '@/graphql/utils/custom_attributes/typeNames';

import Listing from '@/components/listing/Listing.vue';
import { getCellPresenter } from '@/components/listing/AttributeCellPresenterConfig';
import { ColumnSortDirection, SortBy } from '@/components/listing/types';
import { ZenFilter } from '@/components/listing/components/filters/types';

// TODO: Old modals in SPA
import { ToDoListingMixing } from '../OldModalMixin';

// TODO: refactor: reuse almost everything from `SorListing.vue`
export default Vue.extend({
  name: 'TodoListListing',
  components: {
    Listing,
  },
  mixins: [ToDoListingMixing],
  props: {
    category: {
      type: String,
      required: true,
    },
    objectType: {
      // In ModelMetaSchema.modelSingular
      type: String,
      required: true,
    },
    userPreferences: {
      type: Object,
      default: () => ({}),
    },
    context: {
      type: Number,
      default: null,
    },
    listingId: {
      type: String,
      default: 'sor',
    },
  },
  data() {
    return {
      loading: true,
      pageSizes: [20, 50, 100],
      defaultPageSizeWithoutContext: 20,
      defaultPageSizeWithContext: 10,
      currPage: 1,
      totalCount: 0,
      defaultActiveColumns: [],
      userPreferencesData: {},
      columns: [],
      listingData: [],
    };
  },
  computed: {
    ...mapGetters(['currentUser']),
    pluralObjectType() {
      // TODO: this.objectTypeMeta.humanPlural
      let pluralObjectType: string = '';
      if (this.objectType == 'Task') pluralObjectType = 'tasks';
      if (this.objectType == 'Request') pluralObjectType = 'requests';
      if (this.objectType == 'Assessment') pluralObjectType = 'assessments';
      return pluralObjectType;
    },
    computedColumns() {
      if (!this.objectTypeMeta || !this.columnsMeta) return [];

      const columns = flow(
        columnsMeta => columnsMeta.attrs,
        attrs => filter(attrs, attr => includes(this.activeColumns, attr.name)),
        // Sort columns
        attrs => mapOrder(attrs, this.activeColumns, 'name'),
        attrs =>
          map(attrs, attr => ({
            key: attr.name,
            label: attr.displayName,
            customAttributeType: attr.customAttributeType,
            presenter: getCellPresenter(this.objectType, attr.name, attr.customAttributeType),
            sortable: attr.sortable,
            editable: attr.sorEditing,
          }))
      )(this.columnsMeta);

      if (this.objectTypeMeta.isCommentable) {
        // TODO: Make this a selectable column
        columns.unshift({
          key: 'hasUnreadComments',
          label: 'Unread comments',
          customAttributeType: false,
          presenter: 'Comments',
          sortable: false,
          editable: false,
        });
      }

      // TODO: Temporary different title presenter
      find(columns, col => col.key === 'title').presenter = 'TitleWithModal';

      return columns;
    },
    defaultPageSize(): number {
      return this.context ? this.defaultPageSizeWithContext : this.defaultPageSizeWithoutContext;
    },
    // TODO: handle sort by unavailable param for a given object type,
    //       i.e. if manually changing object type in URL
    defaultSort(): SortBy {
      return {
        field: 'endDate',
        direction: ColumnSortDirection.Desc,
      };
    },
    defaultFilters(): object[] {
      return [];
    },

    displayPagination(): boolean {
      return this.totalCount > this.defaultPageSize;
    },

    activePageSize(): void {
      return this.userPreferencesData.pageSize || this.defaultPageSize;
    },
    activeColumns(): string[] {
      return this.userPreferencesData.columns || this.defaultActiveColumns;
    },
    activeSort(): SoryBy {
      if (!this.userPreferencesData['sort']) return this.defaultSort;

      const normalizeSort = sort => ({ field: sort.f, direction: sort.d });

      return normalizeSort(this.userPreferencesData.sort);
    },
    activeFilters(): ZenFilter[] {
      if (!this.userPreferencesData['filters']) return this.defaultFilters;

      // TODO: add normalization to non-abbreviated filters syntax
      const filters = this.userPreferencesData.filters;

      return filters;
    },
  },
  watch: {
    userPreferencesData(newUserPreferences: object, oldUserPreferences: object): void {
      this.persistUserPreferences(newUserPreferences, oldUserPreferences, ['columns']);
      this.$emit('userPreferencesChange', newUserPreferences);
    },
    persistedUserPreferences(persistedUserPreferences: object): void {
      this.userPreferencesData = this.combineUserPreferences(this.userPreferences, persistedUserPreferences);
    },
  },
  apollo: {
    objectTypeMeta: {
      query: gql`
        query ObjectTypeMeta($objectType: SearchType!) {
          objectType(name: $objectType) {
            isCommentable
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
        };
      },
      update: data => data.objectType,
    },
    attributeMeta: {
      fetchPolicy: 'network-only',
      query: gql`
        query Attributes($objectType: SearchType!, $context: AttributesContext!) {
          objectType(name: $objectType) {
            attrs(context: $context) {
              name
              backendKey # TODO: TEMP until changing gql sort query to accept 'by' param in 'name' form
              displayName
            }
          }
        }
      `,
      variables() {
        return {
          context: 'SOR_LISTING',
          category: this.category,
          objectType: this.objectType,
        };
      },
      update: data => keyBy(data.objectType.attrs, 'name'),
    },
    defaultColumns: {
      fetchPolicy: 'network-only',
      query: require('@/graphql/listing/DefaultColumns.gql'),
      variables() {
        return {
          category: this.category,
          type: this.objectType,
        };
      },
      manual: true,
      result({ data }) {
        this.defaultActiveColumns = chain(data.defaultColumns.attrs)
          .filter(attr => attr.sorListingDefault)
          .map(attr => attr.name)
          .value();
      },
    },
    // TODO: code duplication from ColumnSettings.vue
    columnsMeta: {
      fetchPolicy: 'network-only',
      query: require('@/graphql/listing/Columns.gql'),
      variables() {
        return {
          context: 'SOR_LISTING',
          category: this.category,
          type: this.objectType,
          // TOOD: move to gql
          // Necessary for reactivity even though filtering is currently done in result hook
          columns: this.activeColumns,
        };
      },
      update: data => data.objectType,
      // TODO
      // error(error) {
      //   this.error = JSON.stringify(error.message);
      // },
    },
    persistedUserPreferences: {
      fetchPolicy: 'network-only',
      query: gql`
        query UserListingPreferences($state: UserListingPreferencesState!) {
          currentUser {
            listingPreferences(state: $state) {
              columns
            }
          }
        }
      `,
      variables() {
        const state = {
          objectType: this.objectType,
          context: 'TODO',
          view: this.userPreferencesView(this.category, this.context),
        };

        return { state };
      },
      // Unlike on the Listing view, we only care about persisting the columns (for now)
      update: function(data): object {
        return {
          columns: data.currentUser.listingPreferences.columns,
        };
      },
      error() {
        this.showAlert({
          type: 'error',
          message: 'Something went wrong fetching your user preferences.',
        });

        this.setFallbackUserPreferences();
      },
    },
    listingData: {
      fetchPolicy: 'network-only',
      query() {
        const isCommentable: boolean = this.objectTypeMeta.isCommentable;
        const columns: Array<string | object> = this.getColumnsConfig(this.activeColumns, this.attributeMeta);
        const order: string = this.composeOrderSearchQuery(this.activeSort);

        // TODO: this is specific to the 'todo' category
        let contextForObject = this.pluralObjectType;
        if (this.category == 'todo') {
          if (this.pluralObjectType == 'tasks') {
            contextForObject = this.context == 1 ? 'tasksToSubmit' : 'tasksToReviewOrVerify';
          }
          if (this.pluralObjectType == 'requests') {
            contextForObject = this.context == 1 ? 'requestsToSubmit' : 'requestsToReviewOrVerify';
          }
          if (this.pluralObjectType == 'assessments') {
            contextForObject = this.context == 1 ? 'assessmentsToVerify' : 'assessmentsToComplete';
          }
        }

        return ToDoListSearchQuery(
          this.category,
          contextForObject,
          isCommentable,
          [...columns],
          order,
          this.activePageSize,
          this.currPage
        );
      },
      variables() {
        const currentUserFilter: string = this.composeFilterSearchQuery();
        const objectsFilter: string = this.composeFilterObjectsQuery(this.activeFilters);

        return {
          category: this.category,
          objectType: this.objectType,

          currentUserFilter,
          objectsFilter,
          // TOOD: move to gql
          // Necessary for reactivity even though filtering is currently done in result hook
          columns: this.activeColumns,
        };
      },
      skip: function() {
        return (
          this.attributeMeta == null ||
          this.$apollo.queries.attributeMeta.loading ||
          this.$apollo.queries.columnsMeta.loading ||
          this.$apollo.queries.defaultColumns.loading
        );
      },
      update(data) {
        // TODO: this is specific to the 'todo' category
        let contextForObject = this.pluralObjectType;
        if (this.category == 'todo') {
          if (this.pluralObjectType == 'tasks') {
            contextForObject = this.context == 1 ? 'tasksToSubmit' : 'tasksToReviewOrVerify';
          }
          if (this.pluralObjectType == 'requests') {
            contextForObject = this.context == 1 ? 'requestsToSubmit' : 'requestsToReviewOrVerify';
          }
          if (this.pluralObjectType == 'assessments') {
            contextForObject = this.context == 1 ? 'assessmentsToVerify' : 'assessmentsToComplete';
          }
        }

        const dataa = data.search.edges[0].node.todos[this.category][contextForObject];

        this.totalCount = dataa.totalCount;

        /**
         * Adds 'mappings.' prefix to all mapped objects, which corresponds to
         * the column key for a mapped object, i.e.: 'tasks' to 'mappings.tasks'
         */
        const mappedObjectsColumns = mappedObjects =>
          mapKeys(mappedObjects, (_, objectType: string) => `mappings.${objectType}`);

        return map(dataa.edges, edge => ({
          ...edge.node,
          // TODO: don't map __typename
          // TODO: delete the 'mappings' key
          ...mappedObjectsColumns(edge.node.mappings),
        }));
      },
      error() {
        this.showAlert({
          type: 'error',
          message: 'Oops, something went wrong.',
        });
      },
      watchLoading(isLoading: boolean) {
        this.loading = isLoading;
      },
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
    // Called from parent
    refresh(): void {
      this.$apollo.queries.listingData.refetch();
    },
    userPreferencesView(category: string, context: string): string {
      return this.context ? `${category}.view-${context}` : category;
    },
    setFallbackUserPreferences(): void {
      this.userPreferencesData = this.userPreferences;
    },
    combineUserPreferences(rawInputUserPreferences: object, rawPersistedUserPreferences: object): object {
      // Removes preferences which have null value
      const inputUserPreferences: object = pickBy(rawInputUserPreferences, identity);
      const persistedUserPreferences: object = pickBy(rawPersistedUserPreferences, identity);

      // Preferences in persisted user preferences are authoritative
      return { ...inputUserPreferences, ...persistedUserPreferences };
    },
    setUserPreference(queryName: string, query: string): void {
      this.userPreferencesData = query
        ? (this.userPreferencesData = {
            ...this.userPreferencesData,
            ...{ [queryName]: query },
          })
        : omit(this.userPreferencesData, queryName);
    },
    // Differences between new and old are at most by one property
    // TODO: if we need to handle by multiple we should use deep-diff package
    persistUserPreferences(
      newUserPreferences: object,
      oldUserPreferences: object,
      userPreferencesToPersist: string[]
    ): void {
      const oldUserPreferencesToPersist = pick(oldUserPreferences, userPreferencesToPersist);
      const newUserPreferencesToPersist = pick(newUserPreferences, userPreferencesToPersist);

      const newPreferences: string[] = Object.keys(newUserPreferencesToPersist);
      const oldPreferences: string[] = Object.keys(oldUserPreferencesToPersist);

      const deletedPreferences: string[] = difference(oldPreferences, newPreferences);

      if (deletedPreferences.length && newPreferences.length < oldPreferences.length) {
        this.deletePersistedUserPreferences(deletedPreferences);
      } else if (newPreferences.length) {
        this.savePersistedUserPreferences(newUserPreferences);
      }
    },
    savePersistedUserPreferences(userPreferences): void {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation SaveUserListingPreferences(
              $state: UserListingPreferencesState!
              $userPreferences: UserListingPreferencesData!
            ) {
              saveUserListingPreferences(state: $state, data: $userPreferences) {
                result {
                  columns
                }
              }
            }
          `,
          variables: {
            state: {
              objectType: this.objectType,
              context: 'TODO',
              view: this.userPreferencesView(this.category, this.context),
            },
            userPreferences: {
              columns: userPreferences.columns,
            },
          },
        })
        // TODO: consider using the apollo cache and optimisticResponse
        // .then(data => {
        //   if (data.data.saveUserListingPreferences.result) {
        //     console.log('Saving user preferences successful');
        //     console.log('User preferences saved: ', JSON.stringify(userPreferences, null, 2));
        //   }
        // })
        .catch(() => {
          // console.error('Saving user preferences unsuccessful', error);
          this.showAlert('Unable to update your user preferences.');
        });
    },
    deletePersistedUserPreferences(userPreferences: string[]): void {
      const userPreferencesToDelete = [];

      // if (userPreferences.includes('pageSize')) {
      //   userPreferencesToDelete.push('PAGE_SIZE');
      // }
      if (userPreferences.includes('columns')) {
        userPreferencesToDelete.push('COLUMNS');
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteUserListingPreferences(
              $state: UserListingPreferencesState!
              $userPreferencesToDelete: [ListingPreferenceFields]
            ) {
              deleteUserListingPreferences(state: $state, data: $userPreferencesToDelete) {
                result {
                  columns
                }
              }
            }
          `,
          variables: {
            state: {
              objectType: this.objectType,
              context: 'TODO',
              view: this.userPreferencesView(this.category, this.context),
            },
            userPreferencesToDelete,
          },
        })
        // TODO: consider using the apollo cache and optimisticResponse
        // .then(data => {
        //   if (data.data.deleteUserListingPreferences.result) {
        //     console.log('Deleting user preferences successful');
        //     console.log('Prefs deleted: ', JSON.stringify(userPreferencesToDelete, null, 2));
        //   }
        // })
        .catch(() => {
          // console.error('Deleting user preferences unsuccessful', error);
          this.showAlert('Unable to update your user preferences.');
        });
    },

    pageSizeChange(pageSize: number): void {
      this.setUserPreference('pageSize', pageSize);
    },
    columnsReset(): void {
      this.setUserPreference('columns', undefined);
    },
    columnsChange(columns: string[] | null): void {
      this.setUserPreference('columns', columns);
    },
    sortChange(sort: SortBy): void {
      const serializeSort = sort => ({ f: sort.field, d: sort.direction });

      this.setUserPreference('sort', !isEqual(sort, this.defaultSort) ? serializeSort(sort) : undefined);
    },
    filtersChange(filters: ZenFilter[]): void {
      this.setUserPreference('filters', filters.length ? filters : undefined);
    },
    /**
     * @param  columns  columns in name representation
     * @return columns  for regular and mapped columns: key in GraphQL key representation,
     *                  for custom attributes: object with GraphQL key representation for 'name'
     *                                         and GraphQL type name for 'type'
     */
    getColumnsConfig(columns: string[], attributeMeta): Array<string | object> {
      return columns.map(c => {
        const columnName: string = attributeMeta[c].name;

        if (startsWith(c, 'ca__')) {
          // Specify type for custom attribute
          const columnConfig = find(this.columnsMeta.attrs, cc => cc.name == columnName);
          const type: string = getCustomAttributeGqlTypeName(columnConfig.customAttributeType);
          return { name: columnName, type };
        }

        return columnName;
      });
    },
    composeFilterSearchQuery(): string {
      const searchQuery = { left: 'email', op: { name: '=' }, right: this.currentUser.email.value };

      return JSON.stringify(searchQuery);
    },
    composeFilterObjectsQuery(filters: ZenFilter[]): string {
      let searchQuery = {};

      if (filters.length) {
        searchQuery = { left: 'title', op: { name: '~' }, right: filters[0].v };
      }

      return JSON.stringify(searchQuery);
    },
    composeOrderSearchQuery(sort: SortBy): string {
      const attributeMeta = find(this.attributeMeta, a => a.name == sort.field);

      // TODO: TEMP change gql order param to accept 'name' form for 'by' attribute
      return `{ by: "${attributeMeta.backendKey}", direction: ${sort.direction.toUpperCase()} }`;
    },
  },
});
